import { z } from 'zod';

/**
 * System roles a user may hold. ClassGO roles (`SUPER_ADMIN`, `STAFF_ADMIN`) belong to staff users
 * outside any tenant; the rest belong to a school's users. Serialized in Cognito's `custom:roles`.
 */
export const roleNameSchema = z.enum([
  'SUPER_ADMIN',
  'STAFF_ADMIN',
  'SCHOOL_OWNER',
  'SCHOOL_STAFF',
  'TEACHER',
  'STUDENT',
  'GUARDIAN',
]);

/**
 * A system role name.
 */
export type RoleName = z.infer<typeof roleNameSchema>;

/**
 * Colombian school shifts. `unica` is the default for schools with one shift.
 */
export const jornadaSchema = z.enum(['unica', 'manana', 'tarde', 'nocturna']);

/**
 * A jornada value.
 */
export type Jornada = z.infer<typeof jornadaSchema>;

/**
 * Colombian school calendars: A starts in January/February, B in August/September.
 */
export const schoolCalendarSchema = z.enum(['A', 'B']);

/**
 * A calendar value.
 */
export type SchoolCalendar = z.infer<typeof schoolCalendarSchema>;

/**
 * Lifecycle of a school year and of an academic period inside it.
 */
export const academicStatusSchema = z.enum(['planned', 'active', 'closed']);

/**
 * An academic status value.
 */
export type AcademicStatus = z.infer<typeof academicStatusSchema>;

/**
 * Lifecycle of a tenant as managed by ClassGO staff.
 */
export const tenantStatusSchema = z.enum(['active', 'suspended']);

/**
 * The messages of the two `FORBIDDEN` answers that refuse a whole session rather than one action:
 * the school is suspended by ClassGO, or the school disabled the person. The app branches on them
 * to explain the refusal instead of showing a generic error.
 */
export const SESSION_REFUSAL = {
  tenantSuspended: 'Tenant suspended',
  userDisabled: 'User disabled',
} as const;

/**
 * A tenant status value.
 */
export type TenantStatus = z.infer<typeof tenantStatusSchema>;

/**
 * Lifecycle of a school user.
 */
export const userStatusSchema = z.enum(['invited', 'active', 'disabled']);

/**
 * A user status value.
 */
export type UserStatus = z.infer<typeof userStatusSchema>;

/**
 * Kinds of grading scale a school may configure for a school year.
 */
export const gradingScaleKindSchema = z.enum(['numeric', 'qualitative']);

/**
 * A grading scale kind.
 */
export type GradingScaleKind = z.infer<typeof gradingScaleKindSchema>;

/**
 * One identity document type a country recognizes, as stored in its country pack.
 */
export const documentTypeSchema = z.object({
  /**
   * Short code used in data, for example `TI`.
   */
  code: z.string().min(1).max(8),

  /**
   * Human-readable name in the country's language.
   */
  name: z.string().min(1),

  /**
   * Whether the document belongs to minors, to help validation at enrollment.
   */
  forMinors: z.boolean(),
});

/**
 * A document type entry.
 */
export type DocumentType = z.infer<typeof documentTypeSchema>;

/**
 * One band of a national performance scale, for example Colombia's `Superior`.
 */
export const nationalScaleBandSchema = z.object({
  /**
   * Label shown on report cards.
   */
  label: z.string().min(1),

  /**
   * Lower bound on a 0–100 normalized scale, inclusive.
   */
  from: z.number().min(0).max(100),

  /**
   * Upper bound on a 0–100 normalized scale, inclusive.
   */
  to: z.number().min(0).max(100),
});

/**
 * A national scale band.
 */
export type NationalScaleBand = z.infer<typeof nationalScaleBandSchema>;

/**
 * The export convention of every list (scoping 3.17): `format=csv` or `format=xlsx` answers a
 * download with the same filters; `json` (the default) answers the list.
 */
export const exportFormatSchema = z.enum(['json', 'csv', 'xlsx']).default('json');

/**
 * An export format.
 */
export type ExportFormat = z.infer<typeof exportFormatSchema>;

/**
 * A country pack as `GET /school/country-pack` answers it: what the screens need from it.
 */
export type CountryPackView = {
  code: string;
  name: string;
  documentTypes: DocumentType[];
  nationalScale: NationalScaleBand[];
};
